import { supabase } from '../lib/supabase';

export type ServiceType = 'tenancy_review_rewrite' | 'commercial_lease_review';

export interface ServiceOrder {
  id: string;
  user_id: string | null;
  service_type: ServiceType;
  status: string;
  amount: number;
  currency: string;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  document_upload_path: string | null;
  review_result: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  payment_status: string;
  customer_email: string | null;
  browser_session_id: string;
  original_document_text: string | null;
  original_filename: string | null;
  analysis_result: Record<string, unknown> | null;
  rewritten_document: string | null;
  annotations: unknown[];
}

const SESSION_KEY = 'rentfix_browser_session_id';

export function getBrowserSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `bs_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export async function createCheckoutSession(
  serviceType: ServiceType,
  successUrl: string,
  cancelUrl: string,
  customerEmail?: string
): Promise<{ checkoutUrl: string; orderId: string; sessionId: string }> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(
    `${supabaseUrl}/functions/v1/create-service-checkout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        serviceType,
        successUrl,
        cancelUrl,
        browserSessionId: getBrowserSessionId(),
        customerEmail,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Checkout failed with status ${response.status}`);
  }

  return response.json();
}

export async function verifyPayment(orderId: string): Promise<ServiceOrder | null> {
  const maxAttempts = 10;
  const delayMs = 2000;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data, error } = await supabase
      .from('service_orders')
      .select('*')
      .eq('id', orderId)
      .maybeSingle();

    if (error) {
      console.error('Error checking payment:', error);
      throw new Error('Failed to verify payment status');
    }

    if (data && data.payment_status === 'paid') {
      return data as ServiceOrder;
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return null;
}

export async function getServiceOrder(orderId: string): Promise<ServiceOrder | null> {
  const { data, error } = await supabase
    .from('service_orders')
    .select('*')
    .eq('id', orderId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching service order:', error);
    return null;
  }

  return data as ServiceOrder | null;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function updateServiceOrder(
  orderId: string,
  updates: Partial<Pick<ServiceOrder,
    'status' | 'original_document_text' | 'original_filename' |
    'analysis_result' | 'rewritten_document' | 'annotations' | 'completed_at'
  >>
): Promise<ServiceOrder | null> {
  if (!orderId || !UUID_REGEX.test(orderId)) {
    console.warn('Skipping service order update: invalid orderId', orderId);
    return null;
  }

  const { data, error } = await supabase
    .from('service_orders')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId)
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error updating service order:', error);
    throw new Error('Failed to update service order');
  }

  return data as ServiceOrder | null;
}
